import React, { useMemo } from 'react';
import { Card, CardActions, CardMedia, Typography } from '@material-ui/core';
import styles from './styles.module.scss';
import commonStyles from '../../../styles/commonStyles.module.scss';
import { useTranslation } from 'react-i18next';
import { getLocalizedAppDetails, needToUninstallDueToHipaa } from '../../../component-helpers/app-details';
import { AppIcon } from '../../Common/AppIcon';
import { generateRoute } from '../../../utils/location';
import { Link as RouteLink } from 'react-router-dom';
import { StarRating } from '../../StarRating';
import { getStarRating } from '../../../utils/helpers';
import { smoothScrollToElement } from '../../../utils/dom-helpers';
import {
  AppDetailsModal,
  AppDetailsModuleId,
  AppDetailsState,
  AppRelatedTranslationKeys,
} from '../../../pages/AppDetails/types';
import { AppMarketPageIdentifier } from '../../../AppRoutes';
import { UninstallAppButton } from '../AppActions/uninstall';
import { AppActionPostRequestResponseHandler, AppActions } from '../AppActions/types';
import { getMainActionsForApp } from '../../../component-helpers/app-actions';
import { ButtonVariant } from '@clover/web-lib-core/components';
import { OpenAppButton } from '../AppActions/open';
import ctx from 'classnames';
import PreInstallConnectButton from '../AppActions/PreInstallConnectButton';
import { AppOverFlowItem, AppOverFlowMenu } from '../AppOverFlowMenu';

interface ComponentProps {
  ratingsAndReviewsId: AppDetailsModuleId;
  state: AppDetailsState;
  uninstallCallBack: AppActionPostRequestResponseHandler;
  setModalVisibility: (modal: AppDetailsModal) => void;
  gotoPricingModule: () => void;
}

export const AppDetailsHeader: React.FC<ComponentProps> = (
  {
    ratingsAndReviewsId,
    state,
    uninstallCallBack,
    setModalVisibility,
    gotoPricingModule,
  },
): JSX.Element => {
  const { t, i18n } = useTranslation();
  const {
    app,
    subscriptionObj,
    canShowAggregateRating,
  } = state;
  const { developer, aggregateRating } = app!;
  const { trialText, freeVersionText, subscriptionRelatedTextOption, activeMeteredBilling } = subscriptionObj || {};
  const {
    name,
    filenameIconSmall,
    filenameCover,
  } = useMemo(() => getLocalizedAppDetails(app!, i18n.language), [app, i18n.language]);
  const rating = useMemo(() => getStarRating(aggregateRating), [aggregateRating]);
  const nonHipaaCompliantInstalledApp = needToUninstallDueToHipaa(app!);

  const openPricingModal = (): void => {
    setModalVisibility(AppDetailsModal.Pricing);
  }

  const rateTheApp = (): void => {
    smoothScrollToElement(ratingsAndReviewsId);
  };

  const buttons = getMainActionsForApp(app!);

  return (
    <Card square data-testid='app-details-header'>
      {!!filenameCover && <CardMedia src={filenameCover} title={name || ''} component='img' className={styles.headerBanner}/>}
      <CardActions disableSpacing={true} classes={{ root: `${styles.heroFooter} ${styles.flex}` }}>
        <div className={styles.left}>
          <div className={styles.iconContainer}>
            <AppIcon src={filenameIconSmall} name={name} data-testid='app-icon' />
          </div>
          <div className={styles.appInfo}>
            <div className={styles.appName}>
              <Typography variant='h4'>
                {name}
              </Typography>
              <RouteLink to={generateRoute(AppMarketPageIdentifier.DeveloperApps, developer.id)}>
                <Typography variant='body1' gutterBottom align='left' className={styles.developerName} noWrap>
                  {t(AppRelatedTranslationKeys.ByDev, { name: developer.name })}
                </Typography>
              </RouteLink>
            </div>
            {canShowAggregateRating && <StarRating rating={rating} size={'small'} bottomMargin={true} />}
          </div>
        </div>
        <div className={styles.right}>
          {!nonHipaaCompliantInstalledApp && <div className={styles.subscription}>
            {
              freeVersionText ?
                <Typography variant='body1' noWrap style={{ fontWeight: 500 }}>
                  {freeVersionText.normal}
                </Typography> :
                trialText ?
                  <Typography variant='body1' noWrap style={{ fontWeight: 500 }}>
                    {trialText.short}
                  </Typography> : null
            }
            {
              subscriptionRelatedTextOption?.text && (
                subscriptionRelatedTextOption.isALink ?
                  <Typography variant='body1' className={commonStyles.link} noWrap
                              onClick={gotoPricingModule}>
                    {subscriptionRelatedTextOption.text}
                  </Typography> :
                  <Typography variant='body1' className={commonStyles.grayText} noWrap>
                    {subscriptionRelatedTextOption.text}
                  </Typography>
              )
            }
            {
              !!activeMeteredBilling?.length && <Typography variant='body1' className={commonStyles.grayText} noWrap>
                {t('pay_per_action')}
              </Typography>
            }
          </div>}
          {
            buttons.map((button, index) => {
              let actionButton;
              switch (button) {
                case AppActions.PreInstall:
                  actionButton = <PreInstallConnectButton key={button} app={app!} actionHandler={openPricingModal} />;
                  break;
                case AppActions.YelpConnect:
                  actionButton = <PreInstallConnectButton key={button} app={app!} actionHandler={() => setModalVisibility(AppDetailsModal.YelpConnect)} />;
                  break;
                case AppActions.Uninstall:
                  actionButton = <UninstallAppButton
                    key={button}
                    app={app!}
                    actionCallBack={uninstallCallBack}
                  />;
                  break;
                case AppActions.Open:
                  actionButton = <OpenAppButton
                    key={button}
                    className={ctx({ [styles.tertiaryButton]: buttons.length > 1 })}
                    buttonVariant={buttons.length > 1 ? ButtonVariant.TERTIARY : ButtonVariant.PRIMARY}
                    app={app!}
                    actionHandler={openPricingModal}
                  />;
                  break;
              }
              return actionButton;
            })
          }
          <AppOverFlowMenu
            app={app!}
            callbacks={{
              [AppOverFlowItem.Uninstall]: uninstallCallBack,
              [AppOverFlowItem.RateApp]: rateTheApp,
              [AppOverFlowItem.ChangeSubscription]: openPricingModal,
              [AppOverFlowItem.OpenApp]: undefined,
            }}
          />
        </div>
      </CardActions>
    </Card>
  );
};
